import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../utils/Functions";

export function stripHtml(value) {
  return value ? value.replace(/<[^>]+>/g, "") : "";
}

const columns = [
  { id: "index", label: "#", align: "left", width: 56, sortable: false },
  { id: "title", label: "Title", align: "left", sortable: true },
  { id: "category", label: "Categories", align: "left", sortable: false },
  { id: "description", label: "Description", align: "left", sortable: true },
];

// Mirrors WarehouseTable.jsx / WarehouseServicesTable.jsx's shell exactly -
// replaces SingleTemplateLink.jsx's card-per-template list with the same
// table pattern Artikli/Usluge now use. No pagination - Templates already
// has its own server-side title/category search (kept as-is in
// TemplatesTab.jsx), just no paging endpoint. Sorting is client-side on
// the already-fetched (searched) array, same reasoning as Services;
// Categories isn't sortable since it's a multi-value chip list, not a
// single comparable value.
export default function TemplatesTable({
  items,
  isLoading,
  emptyMessage,
  onRowClick,
  order,
  orderBy,
  onSort,
}) {
  const { t } = useTranslation();

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <MUI.TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
        <MUI.Table sx={{ minWidth: 900 }}>
          <MUI.TableHead>
            <MUI.TableRow
              sx={{
                "& th": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "grey.50"
                      : "rgba(255,255,255,0.04)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
              }}
            >
              {columns.map((column) => (
                <MUI.TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.id === "index" ? (
                    ""
                  ) : column.sortable ? (
                    <MUI.TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {t(column.label)}
                    </MUI.TableSortLabel>
                  ) : (
                    t(column.label)
                  )}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={4} sx={{ border: 0, py: 6 }}>
                  <MUI.Stack alignItems="center" justifyContent="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : items.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={4} sx={{ border: 0, py: 3 }}>
                  <MUI.Typography variant="body1" color={"grayText"}>
                    {t(emptyMessage)}
                  </MUI.Typography>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              items.map((item, index) => (
                <MUI.TableRow
                  hover
                  key={item.id}
                  onClick={() => onRowClick(item)}
                  data-testid={`template.table.row.${item.id}`}
                  sx={{
                    cursor: "pointer",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <MUI.TableCell sx={{ color: "text.disabled" }}>
                    {index + 1}
                  </MUI.TableCell>
                  <MUI.TableCell>
                    <MUI.Tooltip title={item.title}>
                      <MUI.Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {trimText(item.title, 30)}
                      </MUI.Typography>
                    </MUI.Tooltip>
                  </MUI.TableCell>
                  <MUI.TableCell>
                    {item.category?.length > 0 ? (
                      <MUI.Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {item.category.map((category) => (
                          <MUI.Chip key={category} label={t(category)} size="small" />
                        ))}
                      </MUI.Stack>
                    ) : (
                      <MUI.Typography variant="body2" color="text.disabled">
                        N/A
                      </MUI.Typography>
                    )}
                  </MUI.TableCell>
                  <MUI.TableCell>
                    {item.description ? (
                      <MUI.Tooltip title={stripHtml(item.description)}>
                        <MUI.Typography variant="body2" color="text.secondary">
                          {trimText(stripHtml(item.description), 40)}
                        </MUI.Typography>
                      </MUI.Tooltip>
                    ) : (
                      <MUI.Typography variant="body2" color="text.disabled">
                        {t("NoDescriptionInTemplate")}
                      </MUI.Typography>
                    )}
                  </MUI.TableCell>
                </MUI.TableRow>
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
